'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

type FormValues = {
  email: string;
  password: string;
};

export default function AdminLoginClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = async (data: FormValues) => {
    setAuthError('');
    setIsLoading(true);
    try {
      const supabase = createClient();
      const { error } = await supabase.auth.signInWithPassword({
        email: data.email,
        password: data.password,
      });
      if (error) {
        setAuthError('Incorrect email or password. Please try again.');
        setIsLoading(false);
        return;
      }
      router.push('/admin-panel');
    } catch {
      setAuthError('An unexpected error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <main
      className="min-h-screen flex flex-col items-center justify-center px-6"
      style={{ backgroundColor: 'var(--background)' }}
    >
      <div
        className="w-full max-w-xs rounded-2xl p-8 border"
        style={{ backgroundColor: 'var(--card)', borderColor: 'var(--border)' }}
      >
        {/* Logo */}
        <div className="flex flex-col items-center mb-6">
          <AppImage
            src="/assets/images/angna-icon-1789527716997.png"
            alt="Angna logo — arch and tulsi mark"
            width={64}
            height={64}
            className="mb-3"
          />
          <h1
            className="font-serif text-2xl font-bold tracking-wide"
            style={{ color: 'var(--primary)' }}
          >
            ANGNA
          </h1>
          <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>
            Admin Access
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4" noValidate>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-semibold mb-1"
              style={{ color: 'var(--foreground)' }}
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              placeholder="admin@example.com"
              className="input-field"
              autoComplete="email"
              {...register('email', { required: 'Email is required' })}
            />
            {errors.email && (
              <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
                {errors.email.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-semibold mb-1"
              style={{ color: 'var(--foreground)' }}
            >
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter admin password"
                className="input-field pr-10"
                autoComplete="current-password"
                {...register('password', { required: 'Password is required' })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                style={{ color: 'var(--muted-foreground)' }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? '🙈' : '👁'}
              </button>
            </div>
            {errors.password && (
              <p className="text-xs mt-1" style={{ color: '#B91C1C' }}>
                {errors.password.message}
              </p>
            )}
          </div>

          {authError && (
            <p
              className="text-xs px-3 py-2 rounded-lg"
              style={{
                color: '#B91C1C',
                backgroundColor: '#FEE2E2',
                border: '1px solid #FECACA',
              }}
            >
              {authError}
            </p>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full py-3 text-sm mt-1"
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg
                  className="animate-spin h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  />
                </svg>
                Signing in…
              </span>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            href="/"
            className="text-xs underline underline-offset-2"
            style={{ color: 'var(--muted-foreground)' }}
          >
            ← Back to store
          </Link>
        </div>
      </div>
    </main>
  );
}